import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AppBar from './AppBar';
import SideDrawer from './SideDrawer';
import DetectionCard from '../cards/DetectionCard';
import ReportsView from '../Reports/Reportslayout';
import IntrusionView from '../Intrusions/IntrusionLayout';
import TimeFilter from '../timefilter';
import useWebSockets from '../../hooks/useWebSocket';
import useDbData from '../../hooks/useDbdata';
import { DETECTION_TYPES, DEFAULT_VISIBLE_CARDS, AREA_TOKEN_TO_LOCATION, LOCATION_TO_AREA_TOKEN, VEHICLE_ALERT_GROUP_TO_AREA, getAreaTokenFromBoomAreaCode } from '../../utils/constants';
import VehicleReportsLayout from "../Vehicle/VehicleReportsLayout";
import { redirectIfInvalid, getLoggedInArea, isAdminUser, getAreaDisplayName, getAuthStatus } from '../../utils/authService';
import HomeDashboard from './HomeDashboard';
import AlertCountSidebar from './AlertCountSidebar';
import BoomBarrierPanel from './BoomBarrierPanel';

import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import VehicleCountLayout from '../Vehicle/VehicleCount';

import RouteIcon from "@mui/icons-material/Route";
import WarningIcon from "@mui/icons-material/Warning";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { getAreaFromGroupId } from '../../utils/constants';
import ImageModal from '../cards/ImageModal';
import VehicleAlertMapModal from '../cards/VehicleAlertMapModal';

const iconComponents = {
  PeopleIcon,
  DirectionsCarIcon,
  PersonIcon,
  SecurityIcon,
  RouteIcon,
  WarningIcon,
};

export const normalizeValue = (value) => {
  if (typeof value === 'string') {
    return value.toUpperCase().trim();
  }
  return value;
};

const CARDS_PER_PAGE = 6;
const PAGE_HOVER_DELAY = 600;
const EDGE_ZONE_WIDTH = 88; 

const DashboardLayout = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, setOpen] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 1200 : true);
  // Auto-collapse the drawer on smaller screens, expand on large ones.
  useEffect(() => {
    setOpen(!isSmallScreen);
  }, [isSmallScreen]);
  const [selectedDetection, setSelectedDetection] = useState(null);
  const [selectedArea, setSelectedArea] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loggedInArea, setLoggedInArea] = useState(null);
  const [isAdmin, setIsAdmin] = useState(true);
  const [tokenAuth, setTokenAuth] = useState(null);
  const [lockView, setLockView] = useState(false);

  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('cclDashboardCardOrder');
      if (saved) {
        const parsed = JSON.parse(saved);
        const validIds = DETECTION_TYPES.map(t => t.id);
        const filtered = parsed.filter(id => validIds.includes(id));
        const missing = validIds.filter(id => !filtered.includes(id));
        return [...filtered, ...missing];
      }
    } catch (e) {
      console.warn('Error loading card order:', e);
    }
    return DETECTION_TYPES.map(t => t.id);
  });

  const [currentPage, setCurrentPage] = useState(0);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const pageHoverTimer = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverPage, setDragOverPage] = useState(null);
  const dragStartPageRef = useRef(null);
  const eventCountsRef = useRef({});

  const [edgeZoneActive, setEdgeZoneActive] = useState(null); 
  const edgeScrollTimer = useRef(null);
  const totalPagesRef = useRef(1);

  useEffect(() => {
    redirectIfInvalid();
    const userArea = getLoggedInArea();
    const userIsAdmin = isAdminUser();
    setLoggedInArea(userArea);
    setIsAdmin(userIsAdmin);
    if (userArea) {
      const areaLocation = AREA_TOKEN_TO_LOCATION[userArea];
      if (areaLocation) {
        setSelectedArea(areaLocation);
      }
    }

    const authStatus = getAuthStatus();
  if (!authStatus.isAdmin && authStatus.area) {
    setTokenAuth({ areaInfo: { area: authStatus.area } });
  }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('cclDashboardCardOrder', JSON.stringify(cardOrder));
    } catch (e) {
      console.warn('Error saving card order:', e);
    }
  }, [cardOrder]);

  const [isReportsView, setIsReportsView] = useState(false);
  const [isIntrusionView, setIsIntrusionView] = useState(false);
  const [isVehicleCountView, setIsVehicleCountView] = useState(false);
  const [isVehicleReportsView, setIsVehicleReportsView] = useState(false);

  const {
    connectionStatus,
    crowdData: liveWebSocketCrowdData,
    vehicleData: liveWebSocketVehicleData,
    personData: liveWebSocketPersonData,
    congestionData: liveWebSocketCongestionData,
    loadedStateData: liveWebSocketLoadedStateData,
    unloadedStateData: liveWebSocketUnloadedStateData,
    safetyHazardData: liveWebSocketSafetyHazardData = [],
    insufficientilluminationData: liveWebSocketInsufficientIlluminationData = [],
    boomBarrierData,
    offRouteData: liveWebSocketOffRouteData = [],
    offAreaData: liveWebSocketOffAreaData = [],
    tamperData: liveWebSocketTamperData = [],
    overSpeedData: liveWebSocketOverSpeedData = [],
    unauthorizedStoppageData: liveWebSocketUnauthorizedStoppageData = [],
    deleteEvent,
    getDataForDetectionType: getWebSocketDataForDetectionType,
    isPaused,
    togglePause,
    pausedTime,
    bufferedEvents,
    getBufferedEventsCount,
    updateEventPriority,
    getEventPriority,
    getLivePrioritizedEventsCount,
    debugPriorityMap,
    getVideoStreamUrlByEvent,
    getCameraByIp,
    isCameraOnline,
    camerasByIp
  } = useWebSockets();

  const {
    isLoading,
    error,
    fetchData,
    getDataForDetectionType: getDbDataForDetectionType,
  } = useDbData();

  const [notifications, setNotifications] = useState([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [selectedNotificationEvent, setSelectedNotificationEvent] = useState(null);
  const [notificationImageSource, setNotificationImageSource] = useState(null);
  const [notificationArea, setNotificationArea] = useState('');
  const [notificationLocation, setNotificationLocation] = useState('');
  const [vehicleAlertModalOpen, setVehicleAlertModalOpen] = useState(false);
  const [selectedVehicleAlert, setSelectedVehicleAlert] = useState(null);
  const processedEventIds = React.useRef(new Set());

  // Open checkpost boom barriers (wbCode starting with 'CP'), scoped to the logged-in user's area.
  // Admins see all areas; area-wise users only see boom barriers within their own area (same as VA/VTS alerts).
  const checkpostBoomAlerts = Array.isArray(boomBarrierData)
    ? boomBarrierData.filter(a => {
        if (!a.wbCode || !a.wbCode.toString().startsWith('CP')) return false;
        if (isAdmin || !loggedInArea) return true;
        return getAreaTokenFromBoomAreaCode(a.areaCode) === loggedInArea;
      })
    : [];
  const boomCount = checkpostBoomAlerts.length;

  // In-memory cache of live events closed this session: key → remark string.
  // Intentionally React state (not localStorage) so it resets on page refresh.
  const [liveClosedCache, setLiveClosedCache] = useState({});
  const handleLiveEventClosed = useCallback((key, remark) => {
    setLiveClosedCache(prev => ({ ...prev, [key]: remark }));
  }, []);

  // In-memory cache of VTS closed events (off-route, off-area, unauthorized stoppage, over-speed, tamper)
  // Key format: `${eventId}_${timestamp}` or similar unique identifier
  // Resets on page refresh like liveClosedCache
  const [vtsClosedCache, setVtsClosedCache] = useState({});
  const handleVTSEventClosed = useCallback((key, remark) => {
    setVtsClosedCache(prev => ({ ...prev, [key]: remark }));
  }, []);

  useEffect(() => {
  if (isPaused || isReportsView || isIntrusionView || isVehicleCountView || isVehicleReportsView) return;

  const userArea = loggedInArea;
  const userIsAdmin = isAdmin;

  const eventTypes = [
    { data: liveWebSocketCrowdData, type: 'cd', label: 'Crowd' },
    { data: liveWebSocketVehicleData, type: 'vd', label: 'Vehicle' },
    { data: liveWebSocketPersonData, type: 'pd', label: 'Person' },
    { data: liveWebSocketCongestionData, type: 'vc', label: 'Congestion' },
    { data: liveWebSocketLoadedStateData, type: 'ls', label: 'Loaded' },
    { data: liveWebSocketUnloadedStateData, type: 'ls', label: 'Unloaded' },
    { data: liveWebSocketOffRouteData, type: 'or', label: 'Off-Route' },
    { data: liveWebSocketOffAreaData, type: 'oa', label: 'Off-Area' },
    { data: liveWebSocketTamperData, type: 'ta', label: 'Tamper' },
    { data: liveWebSocketOverSpeedData, type: 'os', label: 'Over Speed' },
    { data: liveWebSocketUnauthorizedStoppageData, type: 'usp', label: 'Stoppage' },
    { data: liveWebSocketSafetyHazardData, type: 'sh', label: 'Safety Hazard' },
    { data: liveWebSocketInsufficientIlluminationData, type: 'ii', label: 'Insufficient Illumination' }
  ];

  eventTypes.forEach(({ data, type, label }) => {
    if (!Array.isArray(data) || data.length === 0) return;
    const latestEvent = data[0];
    if (!latestEvent.id || processedEventIds.current.has(latestEvent.id)) return;

    // ===== AREA FILTERING FOR NON-ADMIN USERS =====
    if (!userIsAdmin && userArea) {
      // For vehicle alerts (or, ta, oa, os, usp)
      if (type === 'or' || type === 'ta' || type === 'oa' || type === 'os' || type === 'usp') {
        if (type === 'oa' && latestEvent.offAreaForArea) {
          const offAreaForAreaToken = LOCATION_TO_AREA_TOKEN[latestEvent.offAreaForArea];
          if (!offAreaForAreaToken || offAreaForAreaToken !== userArea) {
            processedEventIds.current.add(latestEvent.id);
            return;
          }
        } else {
          const eventGroupId = latestEvent.groupId?.toString();
          if (!eventGroupId) { 
            processedEventIds.current.add(latestEvent.id); 
            return; 
          }
          const vehicleAreaToken = VEHICLE_ALERT_GROUP_TO_AREA[eventGroupId];
          
          // ===== FIX: Handle partial token matching =====
          // userArea might be 'magadh-sanghmitra' while vehicleAreaToken is 'magadh'
          // or userArea might be 'amrapali-chandragupta' while vehicleAreaToken is 'amrapali'
          if (!vehicleAreaToken) {
            processedEventIds.current.add(latestEvent.id);
            return;
          }
          
          // Check if either token contains the other (case-insensitive partial match)
          const normalizedUserArea = userArea.toLowerCase();
          const normalizedVehicleArea = vehicleAreaToken.toLowerCase();
          
          const isMatch = normalizedUserArea.includes(normalizedVehicleArea) || 
                         normalizedVehicleArea.includes(normalizedUserArea) ||
                         normalizedUserArea === normalizedVehicleArea;
          
          if (!isMatch) {
            processedEventIds.current.add(latestEvent.id);
            return;
          }
          // ===== END FIX =====
        }
      } 
      // For camera-based events (cd, vd, pd, vc, ls, sh, ii)
      else {
        const eventArea = latestEvent.area || latestEvent.attributes?.area || '';
        const userAreaLocation = AREA_TOKEN_TO_LOCATION[userArea];
        
        if (!eventArea || !userAreaLocation) {
          processedEventIds.current.add(latestEvent.id);
          return;
        }
        
        // Normalize and compare areas
        const normalizedEventArea = normalizeValue(eventArea);
        const normalizedUserArea = normalizeValue(userAreaLocation);
        
        if (normalizedEventArea !== normalizedUserArea && 
            !normalizedEventArea.includes(normalizedUserArea) && 
            !normalizedUserArea.includes(normalizedEventArea)) {
          processedEventIds.current.add(latestEvent.id);
          return;
        }
      }
    }
    // ===== END AREA FILTERING =====

    processedEventIds.current.add(latestEvent.id);
    if (processedEventIds.current.size > 1000) {
      const idsArray = Array.from(processedEventIds.current);
      processedEventIds.current = new Set(idsArray.slice(-1000));
    }

    let eventType = type;
    if (type === 'pd') {
      eventType = latestEvent.detectionType === 'ID' ? 'id' :
        latestEvent.type === 'ID' ? 'id' :
          latestEvent.originalMessage?.eventType === 'ID' ? 'id' : 'pd';
    }

    const notification = {
      id: latestEvent.id,
      eventId: latestEvent.eventId || latestEvent.id,
      eventType: eventType,
      severity: latestEvent.severity || 'normal',
      location: latestEvent.location || latestEvent.vehicleTransporter || 'Unknown',
      area: latestEvent.area || 'Unknown Area',
      timestamp: latestEvent.timestamp || Date.now(),
      isVehicleAlert: latestEvent.isVehicleAlert || type === 'or' || type === 'oa' || type === 'ta' || type === 'os' || type === 'usp',
      vehicleNumber: latestEvent.vehicleNumber || null,
      cameraIp: latestEvent.cameraIp,
      httpsUrl: latestEvent.httpsUrl,
      imageUrl: latestEvent.imageUrl,
      alertType: latestEvent.alertType,
      fullEvent: latestEvent
    };

    setNotifications(prev => [...prev, notification]);
  });
}, [
  liveWebSocketCrowdData, liveWebSocketVehicleData, liveWebSocketPersonData,
  liveWebSocketCongestionData, liveWebSocketLoadedStateData, liveWebSocketUnloadedStateData,
  liveWebSocketOffRouteData, liveWebSocketOffAreaData, liveWebSocketTamperData,
  liveWebSocketSafetyHazardData, liveWebSocketInsufficientIlluminationData,
  liveWebSocketOverSpeedData, liveWebSocketUnauthorizedStoppageData,
  isPaused, isReportsView, isIntrusionView, isVehicleCountView, isVehicleReportsView,
  loggedInArea, isAdmin
]);

  const reorderCardsForNotification = (eventType) => {
  // If user has locked the view, do nothing
  if (lockView) return;

  const currentOrder = cardOrder;
  const pageStart = currentPage * CARDS_PER_PAGE;
  const pageEnd = pageStart + CARDS_PER_PAGE;
  const currentPageIds = currentOrder.slice(pageStart, pageEnd);

  // Already visible on the current page — nothing to swap
  if (currentPageIds.includes(eventType)) return;

  // Find the current-page card with the fewest events
  let minCount = Infinity;
  let leastEventsCardId = null;

  currentPageIds.forEach(id => {
    const count = eventCountsRef.current[id] ?? 0;
    if (count < minCount) {
      minCount = count;
      leastEventsCardId = id;
    }
  });

  if (!leastEventsCardId) return;

  setCardOrder(prev => {
    const newOrder = [...prev];
    const notifIdx = newOrder.indexOf(eventType);
    const leastIdx = newOrder.indexOf(leastEventsCardId);
    if (notifIdx !== -1 && leastIdx !== -1) {
      [newOrder[notifIdx], newOrder[leastIdx]] = [newOrder[leastIdx], newOrder[notifIdx]];
    }
    return newOrder;
  });
  // No page navigation — user stays on their current page
};

  const handleNotificationClick = async (notification) => {
    const event = notification.fullEvent;
    
    // Reorder cards based on notification type
    reorderCardsForNotification(notification.eventType);
    
    if (event.isVehicleAlert || notification.eventType === 'or' || notification.eventType === 'oa' || notification.eventType === 'ta') {
      setSelectedVehicleAlert(event);
      setVehicleAlertModalOpen(true);
      return;
    }
    try {
      setNotificationArea(event.area || notification.area);
      setNotificationLocation(event.location || notification.location);
      let modalEvent = { ...event };
      if (!isReportsView && getEventPriority) {
        const eventKey = event.eventId || event.id;
        const existingPriority = getEventPriority(eventKey);
        if (existingPriority) {
          modalEvent.severity = existingPriority.severity;
          modalEvent.remarks = existingPriority.remarks || [];
          modalEvent.priorityUpdated = true;
          modalEvent.priorityUpdatedAt = existingPriority.updatedAt;
        }
      }
      setSelectedNotificationEvent(modalEvent);
      if (isReportsView && event.imageUrl) {
        setNotificationImageSource(event.imageUrl);
      } else if (event.httpsUrl && (event.id || event.eventId)) {
        const eventKey = event.eventId || event.id;
        const response = await fetch(`${event.httpsUrl}/api/va/event/?id=${eventKey}`);
        if (response.ok) {
          const blob = await response.blob();
          setNotificationImageSource(URL.createObjectURL(blob));
        }
      }
      setNotificationModalOpen(true);
    } catch (error) {
      console.error('Error opening notification modal:', error);
    }
  };

  const handleNotificationModalClose = () => {
    setNotificationModalOpen(false);
    if (notificationImageSource) {
      URL.revokeObjectURL(notificationImageSource);
      setNotificationImageSource(null);
    }
    setSelectedNotificationEvent(null);
  };

  const handleVehicleAlertModalClose = () => {
    setVehicleAlertModalOpen(false);
    setSelectedVehicleAlert(null);
  };

  const handleNotificationClose = (notificationId) => {
    console.log('Notification closed:', notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const toggleDrawer = () => setOpen(!open);

  const handleDetectionChange = (e) => setSelectedDetection(e.target.value);

  const handleAreaChange = (e) => {
    if (loggedInArea && !isAdmin) return;
    const value = e.target.value;
    if (value === "All" || (Array.isArray(value) && value.includes("All"))) {
      setSelectedArea("All"); return;
    }
    setSelectedArea(value);
    if (Array.isArray(value) && value.length === 0) setSelectedArea("All");
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "All" || (Array.isArray(value) && value.includes("All"))) {
      setSelectedCategory("All"); return;
    }
    setSelectedCategory(value);
    if (Array.isArray(value) && value.length === 0) setSelectedCategory("All");
  };

  const handleViewChange = (view, detectionId = null) => {
    setIsReportsView(view === 'reports');
    setIsIntrusionView(view === 'intrusions');
    setIsVehicleCountView(view === 'vehicleCount');
    setIsVehicleReportsView(view === 'vehicleReports');
    if (view === 'live' && detectionId) {
      setSelectedDetection(detectionId);
    } else if (view === 'home' || (view === 'live' && detectionId === null)) {
      setSelectedDetection(null);
    }
  };

  const handleEventUpdated = (updatedEvent) => {};

  const getIconComponent = (iconName) => iconComponents[iconName] || SecurityIcon;

  const handleDeleteEvent = (typeId) => {
    if (typeof deleteEvent === 'function') {
      deleteEvent(typeId);
      if (bufferedEvents && bufferedEvents[typeId]) bufferedEvents[typeId] = [];
    }
  };

  const getFilteredDataForDetectionType = (typeId) => {
    let data = [];
    switch (typeId) {
      case "cd": data = Array.isArray(liveWebSocketCrowdData) ? liveWebSocketCrowdData : []; break;
      case "vd": data = Array.isArray(liveWebSocketVehicleData) ? liveWebSocketVehicleData : []; break;
      case "pd":
        data = Array.isArray(liveWebSocketPersonData) ?
          liveWebSocketPersonData.filter(e => e.detectionType === 'PD' || e.type === 'PD' || e.originalMessage?.eventType === 'PD') : [];
        break;
      case "id":
        data = Array.isArray(liveWebSocketPersonData) ?
          liveWebSocketPersonData.filter(e => e.detectionType === 'ID' || e.type === 'ID' || e.originalMessage?.eventType === 'ID') : [];
        break;
      case "vc": data = Array.isArray(liveWebSocketCongestionData) ? liveWebSocketCongestionData : []; break;
      case "ls":
        const lsEvents = Array.isArray(liveWebSocketLoadedStateData) ? liveWebSocketLoadedStateData.map(e => ({ ...e, loadingState: 'LS' })) : [];
        const usEvents = Array.isArray(liveWebSocketUnloadedStateData) ? liveWebSocketUnloadedStateData.map(e => ({ ...e, loadingState: 'US' })) : [];
        data = [...lsEvents, ...usEvents];
        break;
      case "or": data = Array.isArray(liveWebSocketOffRouteData) ? liveWebSocketOffRouteData : []; break;
      case "oa": data = Array.isArray(liveWebSocketOffAreaData) ? liveWebSocketOffAreaData : []; break;
      case "ta": data = Array.isArray(liveWebSocketTamperData) ? liveWebSocketTamperData : []; break;
      case "os": data = Array.isArray(liveWebSocketOverSpeedData) ? liveWebSocketOverSpeedData : []; break;
      case "usp": data = Array.isArray(liveWebSocketUnauthorizedStoppageData) ? liveWebSocketUnauthorizedStoppageData : []; break;
      case "sh": data = Array.isArray(liveWebSocketSafetyHazardData) ? liveWebSocketSafetyHazardData : []; break;
      case "ii": data = Array.isArray(liveWebSocketInsufficientIlluminationData) ? liveWebSocketInsufficientIlluminationData : []; break;
      default: data = [];
    }

    if (selectedArea !== "All") {
      const areaArray = Array.isArray(selectedArea) ? selectedArea : [selectedArea];
      data = data.filter((event) => {
        if (event.isVehicleAlert || event.alertType) {
          const eventGroupId = event.groupId?.toString();
          if (!eventGroupId) return false;
          const areaToken = VEHICLE_ALERT_GROUP_TO_AREA[eventGroupId];
          if (!areaToken) return false;
          const areaTokenToLocationMap = {
            'barkasayal': 'Barka Sayal', 'argada': 'Argada', 'northkaranpura': 'North Karanpura',
            'bokarokargali': 'Bokaro & Kargali', 'kathara': 'Kathara', 'giridih': 'Giridih',
            'amrapali': 'Amrapali & Chandragupta', 'magadh': 'Magadh & Sanghmitra',
            'rajhara': 'Rajhara', 'kuju': 'Kuju', 'hazaribagh': 'Hazaribagh',
            'rajrappa': 'Rajrappa', 'dhori': 'Dhori', 'piparwar': 'Piparwar'
          };
          const eventAreaName = areaTokenToLocationMap[areaToken];
          if (!eventAreaName) return false;
          return areaArray.some(s => s === eventAreaName);
        } else {
          const eventArea = event.area || event.attributes?.area || '';
          const normalizedEventArea = normalizeValue(eventArea);
          return areaArray.some(area => {
            const normalizedAreaToMatch = normalizeValue(area);
            return normalizedEventArea.includes(normalizedAreaToMatch) || normalizedAreaToMatch.includes(normalizedEventArea);
          });
        }
      });
    }

    if (selectedCategory !== "All") {
      const categoryArray = Array.isArray(selectedCategory) ? selectedCategory : [selectedCategory];
      data = data.filter((event) => {
        if (event.isVehicleAlert) {
          const alertCategory = event.alertType || 'VEHICLE';
          const normalizedAlertCategory = normalizeValue(alertCategory);
          return categoryArray.some(c => normalizeValue(c) === normalizedAlertCategory || normalizeValue(c) === 'VEHICLE');
        } else {
          const eventCategory = event.category || '';
          return categoryArray.some(c => normalizeValue(c) === normalizeValue(eventCategory));
        }
      });
    }

    return data;
  };

  const getPrioritizedEventsForType = (typeId) => {
    const data = getFilteredDataForDetectionType(typeId);
    return data.filter(event => event.isLivePrioritized || event.priorityUpdated).length;
  };

  const handleBackToLive = () => {
    setIsReportsView(false);
    setIsIntrusionView(false);
    setSelectedDetection(null);
    setIsVehicleCountView(false);
    setIsVehicleReportsView(false);
  };

  const clearEdgeScrollTimer = useCallback(() => {
    if (edgeScrollTimer.current) {
      clearTimeout(edgeScrollTimer.current);
      edgeScrollTimer.current = null;
    }
  }, []);

  const handleEdgeDragOver = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (!draggingId) return;

    if (edgeZoneActive === direction) return;
    setEdgeZoneActive(direction);

    clearEdgeScrollTimer();
    edgeScrollTimer.current = setTimeout(() => {
      if (direction === 'left') {
        setCurrentPage(p => Math.max(0, p - 1));
      } else {
        setCurrentPage(p => Math.min(totalPagesRef.current - 1, p + 1));
      }
      setEdgeZoneActive(null);
    }, PAGE_HOVER_DELAY);
  }, [draggingId, edgeZoneActive, clearEdgeScrollTimer]);

  const handleEdgeDragLeave = useCallback(() => {
    clearEdgeScrollTimer();
    setEdgeZoneActive(null);
  }, [clearEdgeScrollTimer]);

  const handleEdgeDragDrop = useCallback((e) => {
    e.preventDefault();
    clearEdgeScrollTimer();
    setEdgeZoneActive(null);
  }, [clearEdgeScrollTimer]);

  const clearPageHoverTimer = useCallback(() => {
    if (pageHoverTimer.current) {
      clearTimeout(pageHoverTimer.current);
      pageHoverTimer.current = null;
    }
  }, []);

  const handleDragStart = useCallback((e, id) => {
    dragItem.current = id;
    dragStartPageRef.current = currentPage;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }, [currentPage]);

  const handleDragOver = useCallback((e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    clearPageHoverTimer();
    if (dragOverItem.current !== id) {
      dragOverItem.current = id;
      setDragOverId(id);
    }
  }, [clearPageHoverTimer]);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    clearPageHoverTimer();
    clearEdgeScrollTimer();
    setEdgeZoneActive(null);

    const sourceId = dragItem.current;
    if (!sourceId || sourceId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    setCardOrder(prev => {
      const newOrder = [...prev];
      const sourceIdx = newOrder.indexOf(sourceId);
      const targetIdx = newOrder.indexOf(targetId);
      if (sourceIdx === -1 || targetIdx === -1) return prev;
      [newOrder[sourceIdx], newOrder[targetIdx]] = [newOrder[targetIdx], newOrder[sourceIdx]];
      return newOrder;
    });

    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingId(null);
    setDragOverId(null);
  }, [clearPageHoverTimer, clearEdgeScrollTimer]);

  const handleDragEnd = useCallback(() => {
    clearPageHoverTimer();
    clearEdgeScrollTimer();
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingId(null);
    setDragOverId(null);
    setDragOverPage(null);
    setEdgeZoneActive(null);
  }, [clearPageHoverTimer, clearEdgeScrollTimer]);

  const orderedDetectionTypes = cardOrder
    .map(id => DETECTION_TYPES.find(t => t.id === id))
    .filter(Boolean);

  const totalPages = Math.ceil(orderedDetectionTypes.length / CARDS_PER_PAGE);
  totalPagesRef.current = totalPages; 

  const pagedTypes = orderedDetectionTypes.slice(
    currentPage * CARDS_PER_PAGE,
    (currentPage + 1) * CARDS_PER_PAGE
  );

  // ── Update eventCountsRef every render so reorderCardsForNotification
//    always reads the latest counts regardless of closure age ──
let totalActiveEvents = 0;
DETECTION_TYPES.forEach(type => {
  const count = getFilteredDataForDetectionType(type.id).length;
  eventCountsRef.current[type.id] = count;
  totalActiveEvents += count;
});

const bufferedEventsCount = getBufferedEventsCount();
const isInDashboardView = !isReportsView && !isVehicleCountView && !isVehicleReportsView && !isIntrusionView;

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [totalPages, currentPage]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', backgroundColor: '#17212b' }}>
      <AppBar
        open={open}
        toggleDrawer={toggleDrawer}
        selectedArea={selectedArea}
        handleAreaChange={handleAreaChange}
        selectedCategory={selectedCategory}
        handleCategoryChange={handleCategoryChange}
        selectedDetection={selectedDetection}
        handleDetectionChange={handleDetectionChange}
        isReportsView={isReportsView}
        isIntrusionView={isIntrusionView}
        isVehicleCountView={isVehicleCountView}
        isVehicleReportsView={isVehicleReportsView}
        isPaused={isPaused}
        togglePause={togglePause}
        lockView={lockView}
        onToggleLockView={() => setLockView(v => !v)}
        bufferedEventsCount={bufferedEventsCount}
        pausedTime={pausedTime}
        notifications={notifications}
        totalActiveEvents={totalActiveEvents}
        onNotificationClick={handleNotificationClick}
        onNotificationClose={handleNotificationClose}
      />

      <SideDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        selectedView={isReportsView ? 'reports' : isIntrusionView ? 'intrusions' : isVehicleCountView ? 'vehicleCount' : isVehicleReportsView ? 'vehicleReports' : 'live'}
        handleViewChange={handleViewChange}
        isReportsView={isReportsView}
        isVehicleCountView={isVehicleCountView}
        isVehicleReportsView={isVehicleReportsView}
        isIntrusionView={isIntrusionView}
        cardOrder={cardOrder}
        currentPage={currentPage}
        boomBarrierAlerts={checkpostBoomAlerts}
        tokenAuth={isAdmin ? null : tokenAuth}
        isAdmin={isAdmin}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          marginTop: '70px',
          overflow: 'auto',
          backgroundColor: '#17212b',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 70px)'
        }}
      >
        {isInDashboardView ? (
          <Box sx={{
            p: { xs: 1, sm: 1.5, md: 2 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            {error && error.indexOf('404') === -1 && (
              <Alert severity="error" sx={{ mb: 2, backgroundColor: "#2c3e50", color: "white" }}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: "#1ABC9C" }} />
              </Box>
            ) : selectedDetection === null ? (

              /* ── HOME: VA + VTS section overview ── */
              <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>
                <HomeDashboard
                  detectionTypes={DETECTION_TYPES}
                  getCount={(typeId) => getFilteredDataForDetectionType(typeId).length}
                  selectedId={selectedDetection}
                  onSelect={(typeId) => setSelectedDetection(typeId)}
                  boomCount={boomCount}
                  onBoomClick={() => setSelectedDetection('boom')}
                />
              </Box>

            ) : (

              /*
               * ── EXPANDED LAYOUT ──────────────────────────────────────────
               *
               *  ┌───────────────────────────┬──────────────────┐
               *  ┌─────────────────────────┬──────────────────┐
               *  │  Detection card         │  VA cards        │
               *  ├─────────────────────────┤  (full height,   │
               *  │  VTS cards row          │   single column) │
               *  └─────────────────────────┴──────────────────┘
               */
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: '100%', overflow: { xs: 'auto', md: 'hidden' } }}>

                {/* ── LEFT: detection card on top, VTS row below ── */}
                <Box sx={{ flex: 1, minWidth: 0, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', gap: 2, overflowY: { xs: 'visible', md: 'auto' }, overflowX: 'hidden' }}>

                  {/* Detection card — auto height, scrolls internally when events overflow */}
                  {selectedDetection === 'boom' ? (
                    <BoomBarrierPanel
                      boomBarrierAlerts={checkpostBoomAlerts}
                      onClose={() => setSelectedDetection(null)}
                    />
                  ) : (
                    orderedDetectionTypes
                      .filter((type) => type.id === selectedDetection)
                      .map((type) => {
                        const data = getFilteredDataForDetectionType(type.id);
                        const IconComponent = getIconComponent(type.icon);
                        const typeBuffered = bufferedEvents[type.id] ? bufferedEvents[type.id].length : 0;
                        const typePrioritized = getPrioritizedEventsForType(type.id);
                        return (
                          <DetectionCard
                            key={type.id}
                            title={type.title}
                            icon={IconComponent}
                            selected={true}
                            splitView={true}
                            onClick={() => {}}
                            onClose={() => setSelectedDetection(null)}
                            data={data}
                            onDelete={() => handleDeleteEvent(type.id)}
                            totalEvents={data.length}
                            bufferedCount={typeBuffered}
                            isPaused={isPaused}
                            prioritizedCount={typePrioritized}
                            updateEventPriority={updateEventPriority}
                            getEventPriority={getEventPriority}
                            onEventUpdated={handleEventUpdated}
                            isReportView={false}
                            getVideoStreamUrlByEvent={getVideoStreamUrlByEvent}
                            getCameraByIp={getCameraByIp}
                            isCameraOnline={isCameraOnline}
                            vtsClosedCache={vtsClosedCache}
                            onVTSEventClosed={handleVTSEventClosed}
                          />
                        );
                      })
                  )}

                  {/* VTS cards — always visible right below detection card */}
                  <HomeDashboard
                    detectionTypes={DETECTION_TYPES}
                    getCount={(typeId) => getFilteredDataForDetectionType(typeId).length}
                    selectedId={selectedDetection}
                    onSelect={(typeId) => setSelectedDetection(typeId)}
                    vtsOnlyMode={true}
                    boomCount={boomCount}
                    onBoomClick={() => setSelectedDetection('boom')}
                  />

                </Box>

                {/* ── RIGHT: VA cards — full height, single column ── */}
                <AlertCountSidebar
                  detectionTypes={DETECTION_TYPES}
                  getCount={(typeId) => getFilteredDataForDetectionType(typeId).length}
                  selectedId={selectedDetection}
                  onSelect={(typeId) => setSelectedDetection(typeId)}
                />
              </Box>
            )}
          </Box>
        ) : isReportsView ? (
          <ReportsView
            selectedArea={selectedArea}
            selectedCategory={selectedCategory}
            selectedDetection={selectedDetection}
            onBackToLive={handleBackToLive}
            normalizeValue={normalizeValue}
            updateEventPriority={updateEventPriority}
            getEventPriority={getEventPriority}
            onEventUpdated={handleEventUpdated}
          />
        ) : isIntrusionView ? (
          <IntrusionView />
        ) : isVehicleCountView ? (
          <VehicleCountLayout
            selectedArea={selectedArea}
            selectedCategory={selectedCategory}
            selectedDetection={selectedDetection}
            onBackToLive={handleBackToLive}
          />
        ) : isVehicleReportsView ? (
          <VehicleReportsLayout
            selectedArea={selectedArea}
            selectedCategory={selectedCategory}
            onBackToLive={handleBackToLive}
          />
        ) : null}
      </Box>

      {notificationModalOpen && (
        <ImageModal
          isOpen={notificationModalOpen}
          onClose={handleNotificationModalClose}
          imageSource={notificationImageSource}
          area={notificationArea}
          location={notificationLocation}
          event={selectedNotificationEvent}
          onEventUpdated={handleEventUpdated}
          updateEventPriority={updateEventPriority}
          isReportView={isReportsView}
          getVideoStreamUrlByEvent={getVideoStreamUrlByEvent}
          getCameraByIp={getCameraByIp}
          isCameraOnline={isCameraOnline}
          vtsClosedCache={vtsClosedCache}
          onVTSEventClosed={handleVTSEventClosed}
        />
      )}

      {vehicleAlertModalOpen && selectedVehicleAlert && (
        <VehicleAlertMapModal
          open={vehicleAlertModalOpen}
          onClose={handleVehicleAlertModalClose}
          event={selectedVehicleAlert}
          liveClosedCache={liveClosedCache}
          onLiveEventClosed={handleLiveEventClosed}
        />
      )}
    </Box>
  );
};

export default DashboardLayout;